import { useQuery, useQueryClient } from '@tanstack/react-query';
import { uniqBy } from 'lodash';
import { useCallback } from 'react';

import BackendService from '../services/backendService';
import { notUndefined } from '../utils/general';

const GENERATOR_LIMIT = 10;

const retrieveRecentGeneratorsFromStorage = () => {
  try {
    const data = localStorage.getItem('@curipod_v1_recent_generators');
    if (!data) return [];
    const result = JSON.parse(data) as { id: string }[];
    return result;
  } catch (e) {
    return [];
  }
};

const saveToStorage = (ids: { id: string }[]) => {
  localStorage.setItem('@curipod_v1_recent_generators', JSON.stringify(ids));
};

export const useRecentGeneratorsStorage = () => {
  const queryClient = useQueryClient();
  const saveConsumeGeneratorToStorage = useCallback(
    (generatorId: string) => {
      // Grabs the recent and puts the latest one in the front of the array
      const recent = uniqBy(
        [{ id: generatorId }, ...retrieveRecentGeneratorsFromStorage()],
        ({ id }) => id,
      );

      // Always take the first LIMIT of generatorIds from the recent array, and save to storage.
      const last5 = new Array(GENERATOR_LIMIT)
        .fill(0)
        .map((_, i) => recent[i])
        .filter(notUndefined);
      saveToStorage(last5);
      queryClient.invalidateQueries(['recent-generators']);
    },
    [queryClient],
  );

  return {
    saveConsumeGenerator: saveConsumeGeneratorToStorage,
  };
};

const useRecentGenerators = () => {
  const { data: generators, isFetching: isFetchingGenerators } = useQuery(
    ['recent-generators'],
    () => {
      const recent = retrieveRecentGeneratorsFromStorage();
      if (recent.length === 0) return [];
      return BackendService.instance.generators.listByIds(recent.map(s => s.id));
    },
    {
      staleTime: 1000 * 60 * 5,
    },
  );

  return {
    generators,
    isFetchingGenerators,
  };
};

export default useRecentGenerators;
